import React, { useState, useRef, useEffect } from "react";
import { Flex, Box, Text, IconButton } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import JourneyData from "../atoms/JourneyData";
import CardJourney from "../atoms/CardJourney";

const cardWidth = 400;
const gap = 32;
const cardWithGap = cardWidth + gap;

const JourneyCard = ({ journey, isCenter }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{ duration: 0.5 }}
      style={{
        flexShrink: 0,
        width: `${cardWidth}px`,
        transform: isCenter ? "scale(1.05)" : "scale(1)",
        transition: "transform 0.5s ease, all 0.3s ease",
        zIndex: isCenter ? 10 : 1,
      }}
    >
      <Box
        bg="rgba(255, 255, 255, 0.1)"
        p={6}
        rounded="3xl"
        backdropFilter="blur(10px)"
        border={`2px ${isCenter ? "solid rgba(255, 255, 255, 0.7)" : "dashed rgba(255, 255, 255, 0.2)"}`}
        h="full"
        boxShadow={isCenter ? "0 20px 40px rgba(0, 0, 0, 0.3)" : "0 10px 20px rgba(0, 0, 0, 0.2)"}
        opacity={isCenter ? 1 : 0.7}
      >
        <CardJourney
          title={journey.title}
          company={journey.company}
          description={journey.description}
          date={journey.date}
          location={journey.location}
        />
      </Box>
    </motion.div>
  );
};

const Journey = () => {
  const scrollContainerRef = useRef(null);
  const perPage = 1;
  const containerWidth = perPage * cardWidth + (perPage - 1) * gap;
  const offset = containerWidth / 2 - cardWidth / 2;
  const [currentOffset, setCurrentOffset] = useState(JourneyData.length * cardWithGap);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const journeyRef = useRef(null);

  // Create loop by duplicating journey data for seamless scrolling
  // Reverse the data to show latest items first (highest ID first)
  const reversedJourneyData = [...JourneyData].reverse();
  const extendedJourneyData = [
    ...reversedJourneyData,
    ...reversedJourneyData,
    ...reversedJourneyData,
  ];

  // Calculate the center journey index
  const centerIndex =
    (Math.floor(currentOffset / cardWithGap) + Math.floor(perPage / 2)) %
    reversedJourneyData.length;

  const nextJourney = () => {
    if (isTransitioning) return;
    let newOffset = currentOffset + cardWithGap;
    const maxOffset = 2 * reversedJourneyData.length * cardWithGap;
    if (newOffset >= maxOffset) {
      newOffset = reversedJourneyData.length * cardWithGap;
    }
    setCurrentOffset(newOffset);
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const prevJourney = () => {
    if (isTransitioning) return;
    let newOffset = currentOffset - cardWithGap;
    const minOffset = reversedJourneyData.length * cardWithGap;
    if (newOffset < minOffset) {
      newOffset = 2 * reversedJourneyData.length * cardWithGap - cardWithGap;
    }
    setCurrentOffset(newOffset);
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (journeyRef.current) {
        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const clientY = event.touches ? event.touches[0].clientY : event.clientY;
        const { offsetWidth, offsetHeight } = journeyRef.current;
        const x = (clientX / offsetWidth) * 100;
        const y = (clientY / offsetHeight) * 100;

        // Create grainy noise pattern using CSS
        const noiseIntensity = 7.4 + (Math.sin(Date.now() * 0.005) + 1) * 0.05;
        const grainPattern = `
          radial-gradient(circle at ${x}% ${y}%, #115945, #072B21),
          repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, ${noiseIntensity * 0.01}),
            rgba(0, 0, 0, ${noiseIntensity * 0.01}) 1px,
            transparent 1px,
            transparent 30px
          ),
          repeating-linear-gradient(
            90deg,
            rgba(0, 0, 0, ${noiseIntensity * 0.01}),
            rgba(0, 0, 0, ${noiseIntensity * 0.01}) 1px,
            transparent 1px,
            transparent 30px
          ),
          repeating-linear-gradient(
            45deg,
            rgba(0, 0, 0, ${noiseIntensity * 0.005}),
            rgba(0, 0, 0, ${noiseIntensity * 0.005}) 1px,
            transparent 1px,
            transparent 40px
          ),
          repeating-linear-gradient(
            -45deg,
            rgba(0, 0, 0, ${noiseIntensity * 0.005}),
            rgba(0, 0, 0, ${noiseIntensity * 0.005}) 1px,
            transparent 1px,
            transparent 40px
          )
        `;

        journeyRef.current.style.background = grainPattern;
        journeyRef.current.style.backgroundBlendMode =
          "overlay, multiply, multiply, multiply, multiply";
      }
    };

    // Animate the grain effect
    const animateGrain = () => {
      if (journeyRef.current) {
        const currentStyle = journeyRef.current.style.background;
        if (currentStyle.includes("radial-gradient")) {
          // Trigger a re-render of the grain effect
          journeyRef.current.style.background = currentStyle;
        }
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    const grainInterval = setInterval(animateGrain, 50);

    // Initialize background effect at center for mobile
    const fakeEvent = {
      clientX: window.innerWidth / 2,
      clientY: window.innerHeight / 2,
    };
    handlePointerMove(fakeEvent);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      clearInterval(grainInterval);
    };
  }, []);

  return (
    <Box
      ref={journeyRef}
      as="section"
      id="journey"
      className="scroll-section"
      minH="100vh"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      position="relative"
      overflow="hidden"
    >
      <Flex w={"100%"} overflow={"visible"} flexDir={"column"} alignItems="center">
        {/* Title */}
        <motion.div
          initial={{ opacity: 1, transform: "translateY(40px)" }}
          transition={{ ease: "linear", delay: 0.2 }}
          whileInView={{ opacity: 0, transform: "translateY(-40px)" }}
        >
          <Text fontSize="4xl" fontWeight="bold" color="#DD9331" textAlign="center" mb={8}>
            My Journey
          </Text>
        </motion.div>
        {/* Journey Carousel - Loop with responsive per page */}
        <Box
          ref={scrollContainerRef}
          overflow="visible"
          whiteSpace="nowrap"
          py={8}
          w={`${containerWidth}px`}
          mx="auto"
          sx={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Box
            display="flex"
            gap={8}
            flexWrap="nowrap"
            style={{
              width: "fit-content",
              transform: `translateX(-${currentOffset - offset}px)`,
              transition: "transform 0.5s ease-in-out",
            }}
          >
            {extendedJourneyData.map((journey, index) => {
              const realIndex = index % reversedJourneyData.length;
              const isCenter = realIndex === centerIndex;
              return (
                <Box key={`${journey.id}-${index}`} style={{ flexShrink: 0 }}>
                  <Text fontSize="4xl" fontWeight="bold" color="white" textAlign="center" mb={8}>
                    {journey.date}
                  </Text>
                  <Box position="relative" mt={2} mb={8}>
                    <Box borderTop="2px dashed white" w={"107.5%"} />
                    <Box
                      position="absolute"
                      top="0"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      w="20px"
                      h="20px"
                      bg="#115945"
                      borderRadius="50%"
                      border="2px solid white"
                    />
                  </Box>
                  <JourneyCard journey={journey} isCenter={isCenter} />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Animated Timeline and controls */}
        <Flex justify="center" align="center" mt={8} gap={{ base: 2, md: 4 }} px={4}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={prevJourney}
            disabled={isTransitioning}
            bg="rgba(255, 255, 255, 0.1)"
            color="white"
            _hover={{ bg: "rgba(255, 255, 255, 0.2)", transform: "scale(1.1)" }}
            _active={{ bg: "rgba(255, 255, 255, 0.3)" }}
            borderRadius="full"
            size={{ base: "md", md: "lg" }}
            transition="all 0.3s ease"
          />
          {/* Horizontal Animated Progression Line */}
          <Box w="100%" maxW="600px" position="relative">
            <Flex
              justifyContent="space-between"
              alignItems="center"
              px={3}
              gap={{ base: 2, md: 4 }}
            >
              {reversedJourneyData.map((journey, index) => (
                <Box
                  key={index}
                  textAlign="center"
                  w={{ base: "8px", md: "12px" }}
                  h={{ base: "8px", md: "12px" }}
                  borderRadius="50%"
                  bg={index <= centerIndex ? "#A45826" : "rgba(255, 255, 255, 0.9)"}
                  cursor="pointer"
                  zIndex={1}
                  position="relative"
                  onClick={() => {
                    if (isTransitioning) return;
                    const target =
                      reversedJourneyData.length * cardWithGap +
                      (index - Math.floor(perPage / 2)) * cardWithGap;
                    setCurrentOffset(target);
                    setIsTransitioning(true);
                    setTimeout(() => setIsTransitioning(false), 500);
                  }}
                  transition="background-color 0.3s ease"
                />
              ))}
            </Flex>
            {/* Animated Line */}
            <Box
              position="absolute"
              top={{ base: "3px", md: "5px" }}
              left={{ base: "4px", md: "6px" }}
              right={{ base: "4px", md: "6px" }}
              h="2px"
              bg="rgba(255, 255, 255, 0.3)"
              zIndex={0}
            >
              <Box
                h="100%"
                bg="#A45826"
                w={`${(centerIndex / (reversedJourneyData.length - 1)) * 100}%`}
                transition="width 0.5s ease-in-out"
              />
            </Box>
          </Box>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={nextJourney}
            disabled={isTransitioning}
            bg="rgba(255, 255, 255, 0.1)"
            color="white"
            _hover={{ bg: "rgba(255, 255, 255, 0.2)", transform: "scale(1.1)" }}
            _active={{ bg: "rgba(255, 255, 255, 0.3)" }}
            borderRadius="full"
            size={{ base: "md", md: "lg" }}
            transition="all 0.3s ease"
          />
        </Flex>
      </Flex>
    </Box>
  );
};

export default Journey;
